<template>
  <div>
    <div class="flex justify-between items-start gap-lg">
      <h1>Parties</h1>

      <BaseButton
        class="!p-0 ml-auto"
        icon="arrow-down"
        variant="invisible"
        @click.native="showParties = !showParties"
      >
        show json
      </BaseButton>
    </div>

    <div
      v-for="(example, index) in examples"
      :key="index"
      class="p-sm sm:p-lg mb-xxl bg-whiteOverride rounded-sm"
    >
      <h2>
        {{ example.documents.length }}&nbsp;Documents,
        {{ example.numRoles }}&nbsp;Roles,
        {{ example.numParties }}&nbsp;Parties,
        {{ example.numAssignedParties }}&nbsp;Assigned Parties
      </h2>

      <ul class="list-none p-0">
        <li
          v-if="example.parties.value"
          v-for="(party, index) in example.parties.value"
          :key="index"
          class="mb-lg"
        >
          <PartyCard
            :party="party"
            :partyIndex="index"
            :canRemove="example.canRemoveParties.value"
            :validate="() => {}"
            @remove="example.removeParty"
            @toggleCopyOnly="example.toggleCopyOnly"
            :roles="example.roles"
            @toggleRoleSelected="example.toggleRoleSelected"
            @addMyselfAsParty="example.addMyselfAsParty"
          />
        </li>
      </ul>

      <button @click="example.addParty">add party</button>

      <div v-if="showParties">
        <hr />
        <pre
          v-for="(party, index) in example.parties"
          :key="index"
          >{{ JSON.stringify(party, null, 2) }}
        </pre>
      </div>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { ref } from 'vue';
import { faker } from '@faker-js/faker';

import {
  createDocumentRoles,
  createDocuments,
} from '@/data/factories/documents';
import { useParties } from '@/composables/parties/parties';
import { useAssignRoles, useRoles } from '@/composables/roles/roles';
import { createParties } from '@/store/modules/envelopes/factories/parties';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import PartyCard from '@/components/ui/envelopeCreate/PartyCard/PartyCard.vue';

/** @typedef {import('@/data/schema/parties').TNewOrSavedParty} TNewOrSavedParty */
/** @typedef {import('@/data/schema/parties').TCreatingParties} TCreatingParties */
/** @typedef {import('@/data/schema/documents').TDocuments} TDocuments */

/**
 * @param {TCreatingParties} currentParties
 * @param {TDocuments} documents
 */
function createExample(currentParties, documents) {
  const partiesRef = ref(currentParties);
  const documentsRef = ref(documents);

  const {
    parties,
    addParty,
    removeParty,
    canRemoveParties,
  } = useParties(
    partiesRef,
    documentsRef,
  );
  const roles = useRoles(parties, documentsRef);
  const { toggleCopyOnly, toggleRoleSelected } = useAssignRoles(
    parties,
    documentsRef,
  );

  /** @param {number} index */
  function addMyselfAsParty(index) {
    const party = parties.value[index];

    party.title = 'My Self';
    party.email = 'me@email.me';
  }

  return {
    parties,
    documents,
    numRoles: documents
      .map(document => document.parties.length)
      .reduce((a, b) => a + b, 0),
    numParties: currentParties.length,
    numAssignedParties: currentParties.filter(party => party.role_ids && party.role_ids.length > 0).length,
    addParty,
    canRemoveParties,
    removeParty,
    toggleCopyOnly,
    roles,
    toggleRoleSelected,
    addMyselfAsParty,
  };
}

const roles = createDocumentRoles(12);
const examples = [
  createExample(
    createParties(1, [{ role_ids: [], password: undefined }]),
    createDocuments(1, [{ parties: [roles[0], roles[1]] }]),
  ),
  createExample(
    createParties(1, [{ role_ids: [roles[0].id] }]),
    createDocuments(1, [{ parties: [roles[0], roles[1]] }]),
  ),
  createExample(
    createParties(1, [{ role_ids: [] }]),
    createDocuments(1, [{ parties: [roles[2]] }]),
  ),
  createExample(
    createParties(1, [{ role_ids: [] }]),
    createDocuments(1, [{ title: 'Document without roles', parties: [] }]),
  ),
  createExample(
    createParties(4, [
      { role_ids: [roles[3].id] },
      { role_ids: [roles[4].id] },
      { role_ids: [] },
      { role: 'copy', role_ids: [] },
    ]),
    createDocuments(3, [
      { title: faker.lorem.sentence(), parties: [roles[3]] },
      { parties: [roles[4]] },
      { parties: [roles[5]] },
    ]),
  ),
  createExample(
    createParties(2, [
      { role_ids: [roles[6].id] },
      { role_ids: [roles[7].id] },
    ]),
    createDocuments(2, [
      { parties: [roles[6], roles[7]] },
      { parties: [roles[8]] },
    ]),
  ),
  createExample(
    createParties(3, [
      { role_ids: [roles[9].id] },
      {
        role_ids: [roles[10].id, roles[11].id],
      },
      { role_ids: [] },
    ]),
    createDocuments(2, [
      { parties: [roles[9], roles[10]] },
      { parties: [roles[11]] },
    ]),
  ),
];

const showParties = ref(false);
</script>
